import { LCC_CONSTANTS } from './constants.js';
export function getCplZone(cplGbp) {
    if (cplGbp <= LCC_CONSTANTS.CPL_GREEN_MAX)
        return 'green';
    if (cplGbp <= LCC_CONSTANTS.CPL_YELLOW_MAX)
        return 'yellow';
    if (cplGbp <= LCC_CONSTANTS.CPL_ORANGE_MAX)
        return 'orange';
    return 'red';
}
export function getCplZoneLabel(zone) {
    const labels = {
        green: 'GREEN — Scale aggressively',
        yellow: 'YELLOW — Healthy scaling zone',
        orange: 'ORANGE — Aggressive, monitor closely',
        red: 'RED — Kill ad, above margin ceiling',
    };
    return labels[zone];
}
export function getCplAction(cplGbp, daysInZone, leadsVolume) {
    if (leadsVolume < 20) {
        return {
            action: 'HOLD',
            urgency: 'none',
            reason: `Insufficient volume (${leadsVolume} leads) — need 20+ before acting`,
        };
    }
    const zone = getCplZone(cplGbp);
    if (zone === 'red') {
        return { action: 'KILL', urgency: 'immediate', reason: 'CPL above £15 ceiling — unprofitable' };
    }
    if (zone === 'orange' && daysInZone >= 5) {
        return {
            action: 'PAUSE',
            urgency: 'soon',
            reason: `Stuck in orange ${daysInZone} days — investigate before relaunch`,
        };
    }
    if (zone === 'green' && daysInZone >= LCC_CONSTANTS.BUDGET_BUMP_MIN_DAYS_GREEN) {
        return {
            action: 'SCALE',
            urgency: 'this_week',
            reason: `Consistent green ${daysInZone} days — push budget +30%`,
        };
    }
    return { action: 'HOLD', urgency: 'none', reason: 'Within tolerance' };
}
export function evaluateRampGate(currentBudgetDaily, last7DaysCpl, currentWeekCapacityPct) {
    if (currentWeekCapacityPct > LCC_CONSTANTS.CAPACITY_CRITICAL_PCT) {
        return {
            decision: 'HALT',
            reason: `Capacity at ${Math.round(currentWeekCapacityPct * 100)}% — Activate next tech before scaling ads`,
        };
    }
    const greenDays = last7DaysCpl.filter(c => c > 0 && c <= LCC_CONSTANTS.CPL_GREEN_MAX).length;
    const orangeDays = last7DaysCpl.filter(c => c > LCC_CONSTANTS.CPL_YELLOW_MAX && c <= LCC_CONSTANTS.CPL_ORANGE_MAX).length;
    const redDays = last7DaysCpl.filter(c => c > LCC_CONSTANTS.CPL_ORANGE_MAX).length;
    if (redDays > 0) {
        return { decision: 'HALT', reason: `${redDays} days in red zone — diagnose before any budget change` };
    }
    if (orangeDays >= LCC_CONSTANTS.BUDGET_CUT_MIN_DAYS_ORANGE) {
        const newBudget = roundToFive(currentBudgetDaily * (1 - LCC_CONSTANTS.BUDGET_CUT_DECREASE_PCT));
        return { decision: 'CUT', newBudget, reason: `${orangeDays} days in orange — step back` };
    }
    if (greenDays >= LCC_CONSTANTS.BUDGET_BUMP_MIN_DAYS_GREEN) {
        const newBudget = roundToFive(currentBudgetDaily * (1 + LCC_CONSTANTS.BUDGET_BUMP_INCREASE_PCT));
        return { decision: 'BUMP', newBudget, reason: `${greenDays} days in green — scale +30%` };
    }
    return { decision: 'HOLD', reason: 'CPL needs more days in zone before gate clears' };
}
export function calculateUnitEconomics(params) {
    const jobValueGross = params.jobValueGbp ?? LCC_CONSTANTS.AVG_JOB_VALUE_GBP;
    const vatActive = params.vatActive ?? LCC_CONSTANTS.VAT_ACTIVE;
    const jobValueNet = vatActive ? jobValueGross / (1 + LCC_CONSTANTS.VAT_RATE) : jobValueGross;
    const convRate = params.conversionRate ?? LCC_CONSTANTS.LEAD_TO_BOOKING_RATE;
    const leadsPerBooking = 1 / convRate;
    const cpa = params.cplGbp * leadsPerBooking;
    const directCost = jobValueNet * LCC_CONSTANTS.DIRECT_COST_PCT;
    const cacPct = cpa / jobValueNet;
    const profitPerJob = jobValueNet - directCost - cpa;
    return { cpa, cacPct, profitPerJob, leadsPerBooking };
}
export function scoreCreative(metrics) {
    const reasoning = [];
    if (metrics.leads < 20) {
        return {
            overallScore: 0,
            recommendation: 'INSUFFICIENT_DATA',
            reasoning: [`Only ${metrics.leads} leads — need 20+ for confident scoring`],
        };
    }
    let score = 50;
    if (metrics.cpl <= LCC_CONSTANTS.CPL_GREEN_MAX) {
        score += 30;
        reasoning.push(`CPL £${metrics.cpl.toFixed(2)} in green zone`);
    }
    else if (metrics.cpl <= LCC_CONSTANTS.CPL_YELLOW_MAX) {
        score += 10;
        reasoning.push(`CPL £${metrics.cpl.toFixed(2)} in yellow zone`);
    }
    else if (metrics.cpl <= LCC_CONSTANTS.CPL_ORANGE_MAX) {
        score -= 10;
        reasoning.push(`CPL £${metrics.cpl.toFixed(2)} in orange — watch`);
    }
    else {
        score -= 30;
        reasoning.push(`CPL £${metrics.cpl.toFixed(2)} in red — unprofitable`);
    }
    const ltbDiff = metrics.leadToBookRate - LCC_CONSTANTS.LEAD_TO_BOOKING_RATE;
    score += Math.round(ltbDiff * 200);
    if (metrics.leadToBookRate >= LCC_CONSTANTS.LEAD_TO_BOOKING_RATE) {
        reasoning.push(`Lead→book ${Math.round(metrics.leadToBookRate * 100)}% meets target`);
    }
    else {
        reasoning.push(`Lead→book ${Math.round(metrics.leadToBookRate * 100)}% below 30% target`);
    }
    score = Math.max(0, Math.min(100, score));
    let recommendation;
    if (score >= 75)
        recommendation = 'SCALE';
    else if (score >= 45)
        recommendation = 'HOLD';
    else
        recommendation = 'KILL';
    return { overallScore: score, recommendation, reasoning };
}
export function formatGbp(amount) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
}
export function formatPct(value) {
    return `${Math.round(value * 100)}%`;
}
function roundToFive(n) {
    return Math.round(n / 5) * 5;
}
//# sourceMappingURL=decisions.js.map